// Determines what operators can be used for a given property type

import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import * as Operators from 'customer-data-filters-ui/filterQueryFormat/operator/Operators';
import { BOOLEAN, DATE, DATE_TIME, ENUMERATION, NUMBER, STRING } from 'customer-data-objects/property/PropertyTypes';
import { OrderedSet } from 'immutable';
import { PRIVACY_CONSENT } from '../listSegClassic/ListSegConstants';
import { getDynamicFilterFamilyBasis } from 'customer-data-filters-ui/filterQueryFormat/DynamicFilterFamilies';
import * as UnifiedEventPropertyTypes from 'customer-data-filters-ui/filterQueryFormat/UnifiedEventPropertyTypes';
import { isAnyEventTypeId } from 'customer-data-filters-ui/filterQueryFormat/ObjectTypeId';
import { hasDateTimeHoursFiltering, hasILSPresetDynamicDateFilters, hasNewIlsTimeFiltersEnabled } from 'customer-data-filters-ui/permissions/gates';
import memoize from 'transmute/memoize';
import invariant from 'react-utils/invariant';
import { COMMUNICATION_SUBSCRIPTION } from './ObjectSegFilterTypes';
import { PHONE_NUMBER } from '../../constants/aditionalPropertyTypes';
const getNumericOperators = () => {
  return OrderedSet.of(Operators.Equal, Operators.NotEqual, Operators.Less, Operators.LessOrEqual, Operators.Greater, Operators.GreaterOrEqual, Operators.InRange, Operators.Known, Operators.NotKnown);
};
const getStaticDateTimeOperators = (isUngated, options) => {
  if (hasNewIlsTimeFiltersEnabled(isUngated)) {
    if (hasDateTimeHoursFiltering(isUngated) && options && options.useHoursFiltering) {
      return OrderedSet.of(Operators.EqualTimePoint, Operators.BeforeTimePointWithTime, Operators.AfterTimePointWithTime, Operators.BetweenTimePointsWithTime, Operators.NotBetweenTimePointsWithTime, Operators.LessRollingWithOrWithoutYear);
    } else {
      return OrderedSet.of(Operators.EqualTimePoint, Operators.BeforeTimePoint, Operators.AfterTimePoint, Operators.BetweenTimePoints, Operators.NotBetweenTimePoints, Operators.LessRollingWithOrWithoutYear);
    }
  } else {
    return OrderedSet.of(Operators.Equal, Operators.BeforeDate, Operators.AfterDate, Operators.InRange, Operators.NotInRange, Operators.LessRolling);
  }
};
const getInDynamicDateRangeOperator = isUngated => {
  if (hasNewIlsTimeFiltersEnabled(isUngated) && hasILSPresetDynamicDateFilters(isUngated)) {
    return OrderedSet.of(Operators.InDynamicDateRange);
  } else {
    return OrderedSet.of();
  }
};
const getSurveyMonkeyQuestionOperators = (type, isUngated, options) => {
  switch (type) {
    case ENUMERATION:
      return OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown);
    case NUMBER:
      return getNumericOperators();
    case STRING:
      return OrderedSet.of(Operators.EqualAny, Operators.NotEqualAny, Operators.ContainAny, Operators.NotContainAny, Operators.StartsWithAny, Operators.EndsWithAny, Operators.Known, Operators.NotKnown);
    case DATE_TIME:
      return getStaticDateTimeOperators(isUngated, options).concat(OrderedSet.of(Operators.GreaterRolling, Operators.LessRolling));
    default:
      return undefined;
  }
};
const getAdsPropertyOperators = (type, __isUngated, __options) => {
  switch (type) {
    case DATE:
      return OrderedSet.of(Operators.Less, Operators.Greater, Operators.InRange, Operators.NotInRange, Operators.GreaterThanRolling, Operators.LessThanRolling);
    case DATE_TIME:
      return OrderedSet.of(Operators.Less, Operators.Greater, Operators.InRange, Operators.NotInRange, Operators.GreaterThanRolling, Operators.LessThanRolling);
    case ENUMERATION:
      return OrderedSet.of(Operators.EqualAny, Operators.Known);
    case STRING:
      return OrderedSet.of(Operators.EqualAny, Operators.ContainAny, Operators.StartsWithAny, Operators.EndsWithAny, Operators.Known);
    default:
      return undefined;
  }
};
const getIntegrationPropertyOperators = (type, isUngated, options) => {
  switch (type) {
    case BOOLEAN:
      return OrderedSet.of(Operators.Equal, Operators.NotEqual, Operators.Known, Operators.NotKnown);
    case DATE:
    case DATE_TIME:
      return getStaticDateTimeOperators(isUngated, options).concat(OrderedSet.of(Operators.GreaterRolling, Operators.LessRollingWithOrWithoutYear, Operators.Known, Operators.NotKnown));
    case ENUMERATION:
      return OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown);
    case NUMBER:
      return getNumericOperators();
    case STRING:
      return OrderedSet.of(Operators.EqualAny, Operators.NotEqualAny, Operators.ContainAny, Operators.NotContainAny, Operators.StartsWithAny, Operators.EndsWithAny, Operators.Known, Operators.NotKnown);
    default:
      return undefined;
  }
};
const getUnifiedEventPropertyOperators = (type, isUngated, options) => {
  switch (type) {
    case BOOLEAN:
      return OrderedSet.of(Operators.Equal, Operators.NotEqual, Operators.Known, Operators.NotKnown);
    case DATE:
    case DATE_TIME:
      return getStaticDateTimeOperators(isUngated, options).concat(OrderedSet.of(Operators.GreaterRolling, Operators.LessRollingWithOrWithoutYear, Operators.Known, Operators.NotKnown));
    case ENUMERATION:
      return OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown);
    case NUMBER:
      return getNumericOperators();
    case STRING:
      return OrderedSet.of(Operators.EqualAny, Operators.NotEqualAny, Operators.ContainAny, Operators.NotContainAny, Operators.StartsWithAny, Operators.EndsWithAny, Operators.Known, Operators.NotKnown);
    case UnifiedEventPropertyTypes.OBJECT_COORDINATES:
      return OrderedSet.of(Operators.EqualAny, Operators.NotEqualAny, Operators.Known, Operators.NotKnown);
    case UnifiedEventPropertyTypes.PROPERTY_CHANGED_NAME_AND_VALUE:
      return OrderedSet.of(Operators.PropertyNameAndValue);
    default:
      return undefined;
  }
};
const FamilyAndTypeToOperatorGetter = new Map([[DSAssetFamilies.SURVEY_MONKEY_QUESTION, getSurveyMonkeyQuestionOperators], [DSAssetFamilies.ADS_PROPERTY, getAdsPropertyOperators], [DSAssetFamilies.INTEGRATION, getIntegrationPropertyOperators], [DSAssetFamilies.UNIFIED_EVENT, getUnifiedEventPropertyOperators]]);
const getOperatorsForType = (type, isUngated, options) => {
  switch (type) {
    case BOOLEAN:
      return OrderedSet.of(Operators.Equal, Operators.NotEqual, Operators.Known, Operators.NotKnown, Operators.EverEqual, Operators.NeverEqual, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays, Operators.UpdatedAfter, Operators.UpdatedBefore);
    case DATE:
      if (options !== null && options !== void 0 && options.isRelativeTimeProperty) {
        return OrderedSet.of(Operators.Less, Operators.LessOrEqual, Operators.Greater, Operators.GreaterOrEqual, Operators.Equal, Operators.NotEqual, Operators.Known, Operators.NotKnown);
      }
      return getInDynamicDateRangeOperator(isUngated).concat(getStaticDateTimeOperators(isUngated, options).concat(OrderedSet.of(Operators.GreaterRolling, Operators.Known, Operators.NotKnown, Operators.After, Operators.Before, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays, Operators.UpdatedAfter, Operators.UpdatedBefore)));
    case DATE_TIME:
      if (options !== null && options !== void 0 && options.isRelativeTimeProperty) {
        return OrderedSet.of(Operators.Less, Operators.Greater, Operators.Known, Operators.NotKnown);
      }
      return getInDynamicDateRangeOperator(isUngated).concat(getStaticDateTimeOperators(isUngated, options).concat(OrderedSet.of(Operators.GreaterRolling, Operators.Known, Operators.NotKnown, Operators.After, Operators.Before, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays, Operators.UpdatedAfter, Operators.UpdatedBefore)));
    case ENUMERATION:
      return OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown, Operators.EverIn, Operators.NeverIn, Operators.EqualAll, Operators.NotEqualAll, Operators.ContainAll, Operators.NotContainAll, Operators.EverEqualAll, Operators.NeverEqualAll, Operators.EverContainedAll, Operators.NeverContainedAll, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays, Operators.UpdatedAfter, Operators.UpdatedBefore);
    case NUMBER:
      return getNumericOperators().concat(OrderedSet.of(Operators.EverEqual, Operators.NeverEqual, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays, Operators.UpdatedAfter, Operators.UpdatedBefore));
    case PHONE_NUMBER:
    case STRING:
      if (options !== null && options !== void 0 && options.isRichTextFieldType) {
        return OrderedSet.of(Operators.ContainAny, Operators.NotContainAny, Operators.StartsWithAny, Operators.EndsWithAny, Operators.Known, Operators.NotKnown, Operators.EverContained, Operators.NeverContained, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays, Operators.UpdatedAfter, Operators.UpdatedBefore);
      }
      return OrderedSet.of(Operators.EqualAny, Operators.NotEqualAny, Operators.ContainAny, Operators.NotContainAny, Operators.StartsWithAny, Operators.EndsWithAny, Operators.Known, Operators.NotKnown, Operators.EverEqualAny, Operators.NeverEqualAny, Operators.EverContained, Operators.NeverContained, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays, Operators.UpdatedAfter, Operators.UpdatedBefore);
    case DSAssetFamilies.CTA:
      return OrderedSet.of(Operators.CtaHasClicked, Operators.CtaHasNotClicked, Operators.CtaHasSeen, Operators.CtaHasNotSeen);
    case DSAssetFamilies.GOTO_WEBINAR_WEBINAR:
      return OrderedSet.of(Operators.WebinarHasAttended, Operators.WebinarNotHasAttended, Operators.WebinarHasRegistered, Operators.WebinarNotHasRegistered);
    case DSAssetFamilies.IN_LIST:
      return OrderedSet.of(Operators.ListIsMember, Operators.ListIsNotMember);
    case DSAssetFamilies.IMPORT:
      return OrderedSet.of(Operators.InImport, Operators.NotInImport);
    case DSAssetFamilies.PAGE_VIEW:
      return OrderedSet.of(Operators.PageViewEqual, Operators.PageViewNotEqual, Operators.PageViewContain, Operators.PageViewNotContain, Operators.PageViewMatchRegex, Operators.PageViewNotMatchRegex);
    case DSAssetFamilies.SURVEY_MONKEY_SURVEY:
      return OrderedSet.of(Operators.SurveyMonkeyHasResponded, Operators.SurveyMonkeyNotHasResponded);
    case DSAssetFamilies.FORM:
      return OrderedSet.of(Operators.FormFilledOut, Operators.FormNotFilledOut);
    case DSAssetFamilies.EVENT:
      return OrderedSet.of(Operators.EventCompleted, Operators.EventNotCompleted);
    case DSAssetFamilies.EMAIL_SUBSCRIPTION:
      return OrderedSet.of(Operators.EmailSubscriptionOptIn, Operators.EmailSubscriptionOptOut, Operators.EmailSubscriptionNotOpted);
    case COMMUNICATION_SUBSCRIPTION:
      return OrderedSet.of(Operators.CommunicationSubscriptionOptIn, Operators.CommunicationSubscriptionOptOut, Operators.CommunicationSubscriptionNotOpted);
    case DSAssetFamilies.EMAIL_CAMPAIGN:
      return OrderedSet.of(Operators.EmailLinkClicked, Operators.EmailMarked, Operators.EmailOpened, Operators.EmailOpenedButLinkNotClicked, Operators.EmailOpenedButNotReplied, Operators.EmailReplied, Operators.EmailUnsubscribed, Operators.EmailBounced, Operators.EmailReceived, Operators.EmailReceivedButNotOpened, Operators.EmailSent, Operators.EmailSentButLinkNotClicked, Operators.EmailSentButNotReceived);
    case DSAssetFamilies.WORKFLOW:
      return OrderedSet.of(Operators.WorkflowActive, Operators.WorkflowNotActive, Operators.WorkflowCompleted, Operators.WorkflowNotCompleted, Operators.WorkflowEnrolled, Operators.WorkflowNotEnrolled, Operators.WorkflowMetGoal, Operators.WorkflowNotMetGoal);
    default:
      return undefined;
  }
};
const privacyConcentOperators = OrderedSet.of(Operators.PrivacyConsentCompleted, Operators.PrivacyConsentNotCompleted);
const getEffectiveFilterFamily = filterFamily => {
  if (isAnyEventTypeId(filterFamily)) {
    return DSAssetFamilies.UNIFIED_EVENT;
  }
  const dynamicFilterFamilyBasis = getDynamicFilterFamilyBasis(filterFamily);
  if (dynamicFilterFamilyBasis) {
    return dynamicFilterFamilyBasis;
  }
  return filterFamily;
};
export const getObjectSegOperatorsForTypeStrict = memoize((type, filterFamily, isUngated, options) => {
  invariant(typeof isUngated === 'function', `Expected ${isUngated} to be a function`);
  const effectiveFilterFamily = getEffectiveFilterFamily(filterFamily);
  const typeName = getDynamicFilterFamilyBasis(type) || type;
  if (effectiveFilterFamily === DSAssetFamilies.CONTACT_PROPERTY && type === PRIVACY_CONSENT) {
    return privacyConcentOperators;
  }
  const getOperators = FamilyAndTypeToOperatorGetter.get(
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
  effectiveFilterFamily);
  if (getOperators) {
    return getOperators(type, isUngated, options);
  }
  return getOperatorsForType(type, isUngated, options) || getOperatorsForType(typeName, isUngated, options);
});